import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import type {
  Content,
  StyleDictionary,
  TDocumentDefinitions,
} from 'pdfmake/interfaces';

import { CfsApi, KaseApi, KasePropertyApi } from '@/api';
import { LocalStorageItems } from '@/constants';
import {
  CFSEvent,
  IAgency,
  INarrative,
  IUser,
  IKaseMasterName,
  IKaseMasterVehicle,
} from '@/models';
import { buildCFSReportDefinition } from '@/pages/CFS/reports/cfsReport/buildCFSReportDefinition';
import {
  IKasePrintFields,
  IKasePrintModalForm,
  KasePrintFieldSections,
  KasePrintFields,
  KasePrintSections,
} from '@/pages/Kases/Components/KasePrintModal/data';
import { buildPDFDefinition } from '@/pages/Kases/Components/KasePrintModal/utils/buildPDFDefinition';

export interface RelatedRecordPrintRow {
  _id: string;
  name?: string;
  type?: string;
}

interface RelatedRecordsBatchPrintParams {
  rows: RelatedRecordPrintRow[];
  user: IUser | null;
  currentAgency: IAgency | null;
  agencies: IAgency[];
  fetchAgency: (id: string) => Promise<IAgency | undefined>;
}

interface RecordSectionDefinition {
  content: Content[];
  header?: Content | null;
  styles?: StyleDictionary;
  defaultStyle?: TDocumentDefinitions['defaultStyle'];
}

export interface RelatedRecordsBatchPrintResult {
  failedRecords: string[];
  printedCount: number;
}

const BATCH_PAGE_MARGINS: [number, number, number, number] = [20, 125, 20, 30];

const normalizeRecordType = (type?: string) =>
  type
    ?.trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

const isCaseRecord = (type?: string) => {
  const normalizedType = normalizeRecordType(type);
  return normalizedType === 'KASE' || normalizedType === 'CASE';
};

const isCfsRecord = (type?: string) => normalizeRecordType(type) === 'CFS';

const toContentArray = (content: TDocumentDefinitions['content']) => {
  if (!content) return [];
  return Array.isArray(content) ? (content as Content[]) : [content as Content];
};

const normalizeCaseSignatureSection = (content: Content[]): Content[] => {
  if (!content.length) {
    return content;
  }

  const lastIndex = content.length - 1;
  const lastSection = cloneDeep(content[lastIndex] as any);
  if (!lastSection || typeof lastSection !== 'object') {
    return content;
  }

  const stack = Array.isArray(lastSection.stack) ? lastSection.stack : null;
  if (!stack?.length) {
    return content;
  }

  const hasPrimaryOfficer = stack.some(
    (item: any) => item?.text === 'Primary Officer:',
  );
  const hasSupervisor = stack.some(
    (item: any) => item?.text === 'Supervisor Approving:',
  );

  if (!hasPrimaryOfficer || !hasSupervisor) {
    return content;
  }

  // Keep both signature lines on the same page in batch print.
  lastSection.unbreakable = true;

  const nextContent = [...content];
  nextContent[lastIndex] = lastSection as Content;
  return nextContent;
};

const resolveHeaderContent = (
  header: TDocumentDefinitions['header'],
): Content | null => {
  if (!header) return null;

  if (typeof header === 'function') {
    const generated = header(1, 1, undefined as never);
    return generated ? (generated as Content) : null;
  }

  return header as Content;
};

const getAgencyData = async ({
  currentAgency,
  fetchAgency,
}: {
  currentAgency: IAgency | null;
  fetchAgency: (id: string) => Promise<IAgency | undefined>;
}) => {
  if (currentAgency?._id) {
    return currentAgency;
  }

  const agencyId = localStorage.getItem(LocalStorageItems.CurrentAgencyId);
  if (!agencyId) {
    return null;
  }

  return (await fetchAgency(agencyId)) ?? null;
};

const buildOfficersData = (kase: any) => {
  if (!kase) return [];

  const officers: Array<Record<string, string>> = [];
  const primaryOfficer = kase.primaryOfficer;
  const primaryInvestigator = kase.primaryInvestigator;
  const assistingOfficers = kase.assistingOfficers || [];

  if (primaryOfficer?.fullName) {
    officers.push({
      rank: primaryOfficer.profile?.employmentInformation?.rank?.name || '/',
      name: primaryOfficer.fullName,
      role: 'Primary Officer',
      badge: primaryOfficer.profile?.employmentInformation?.badgeNumber || '/',
    });
  }

  if (primaryInvestigator?.fullName) {
    officers.push({
      rank:
        primaryInvestigator.profile?.employmentInformation?.rank?.name || '/',
      name: primaryInvestigator.fullName,
      role: 'Primary Investigator',
      badge:
        primaryInvestigator.profile?.employmentInformation?.badgeNumber || '/',
    });
  }

  assistingOfficers.forEach((officer: any) => {
    if (officer?.fullName) {
      officers.push({
        rank: officer.profile?.employmentInformation?.rank?.name || '/',
        name: officer.fullName,
        role: 'Assisting Officer',
        badge: officer.profile?.employmentInformation?.badgeNumber || '/',
      });
    }
  });

  return officers;
};

const createKaseFieldRecord = (
  allModalData: Record<string, (Record<string, any> | null | undefined)[]>,
) => {
  const record: Record<string, Record<number, IKasePrintFields[]>> = {};

  KasePrintFieldSections.forEach((section) => {
    const sectionKey = section.value as KasePrintSections;
    const templateFields = KasePrintFields[sectionKey] || [];

    if (!section.multipleData) {
      record[sectionKey] = {
        [0]: templateFields.map((field) => ({
          ...field,
          redacted: false,
          redactedWords: new Set(),
        })),
      };
      return;
    }

    const items = allModalData[sectionKey] || [];
    record[sectionKey] = {};

    items.forEach((_item, index) => {
      record[sectionKey][index] = templateFields.map((field) => ({
        ...field,
        redacted: false,
        redactedWords: new Set(),
      }));
    });
  });

  return record;
};

const buildCaseSectionDefinition = async ({
  recordId,
  agencyData,
  user,
}: {
  recordId: string;
  agencyData: IAgency | null;
  user: IUser | null;
}): Promise<RecordSectionDefinition> => {
  const [
    kaseResponse,
    kaseMasterNamesResponse,
    kaseMasterVehiclesResponse,
    linkedNarrativesResponse,
    offensesResponse,
    linkedCfsResponse,
    propertiesResponse,
  ] = await Promise.all([
    KaseApi.getOne(recordId),
    KaseApi.getKaseMasterNames(recordId),
    KaseApi.getKaseMasterVehicles(recordId),
    KaseApi.getLinkedNarratives(recordId),
    KaseApi.getOffenses(recordId),
    KaseApi.getLinkedCFS(recordId),
    KasePropertyApi.list(recordId),
  ]);

  const kase = kaseResponse.data;
  const kaseMasterNames = kaseMasterNamesResponse.data || [];
  const kaseMasterVehicles = kaseMasterVehiclesResponse.data || [];
  const linkedNarratives = linkedNarrativesResponse.data || [];
  const offenses = offensesResponse.data?.results || [];
  const linkedCfs = linkedCfsResponse.data || [];
  const kaseProperties = (propertiesResponse.data || []).map((item: any) => {
    if (item?.vehicle?.masterVehicle) {
      return { ...item, ...item.vehicle.masterVehicle };
    }
    return item;
  });

  let activeCFSData: any;
  let activeCFSHistory: any[] = [];
  const firstLinkedCfs = linkedCfs[0]?.cfs?._id;

  if (firstLinkedCfs) {
    const [activeCfsResponse, cfsHistoryResponse] = await Promise.all([
      CfsApi.getOne(firstLinkedCfs),
      CfsApi.getCfsHistory(firstLinkedCfs, {
        limit: 1000,
        sort: JSON.stringify({ createdAt: -1 }),
      }),
    ]);

    activeCFSData = activeCfsResponse.data;
    const historyEvents = [
      CFSEvent.CLOSED,
      CFSEvent.UNIT_SHIFT_ASSIGNED,
      CFSEvent.UNIT_SHIFT_IS_EN_ROUTE,
      CFSEvent.UNIT_SHIFT_IS_ON_SCENE,
      CFSEvent.UNIT_SHIFT_IS_IN_PROGRESS,
      CFSEvent.UNIT_SHIFT_CANCELED,
      CFSEvent.UNIT_SHIFT_COMPLETED,
    ];

    activeCFSHistory =
      cfsHistoryResponse.data?.results?.filter((log: any) =>
        historyEvents.includes(log.event),
      ) || [];
  }

  const officersData = buildOfficersData(kase);
  const allModalData: Record<
    string,
    (Record<string, any> | null | undefined)[]
  > = {
    [KasePrintSections.CaseOverview]: [{ ...kase }],
    [KasePrintSections.DispatchInfo]: [
      {
        ...activeCFSData,
        cfsHistoryLogs: activeCFSHistory,
        start: activeCFSData?.isReadyToDispatch
          ? activeCFSData?.wasReadyToDispatchAt || null
          : null,
        end:
          activeCFSData?.isClosed && activeCFSHistory
            ? activeCFSHistory.find((log) => log.event === CFSEvent.CLOSED)
                ?.createdAt || null
            : null,
      },
    ],
    [KasePrintSections.AuditDetails]: [{ recipient: '', reason: '' }],
    [KasePrintSections.EmailDetails]: [{ emailAddress: '', emailBody: '' }],
    [KasePrintSections.Officers]: officersData,
    [KasePrintSections.NamesList]: (kaseMasterNames as IKaseMasterName[]).map(
      (data) => ({
        ...data.masterName,
        involvement: Array.isArray(data?.relationship?.involvement)
          ? data.relationship.involvement.join(', ')
          : data?.relationship?.involvement ?? '',
      }),
    ),
    [KasePrintSections.VehiclesList]: (
      kaseMasterVehicles as IKaseMasterVehicle[]
    ).map((data) => data.masterVehicle),
    [KasePrintSections.PropertyList]: kaseProperties,
    [KasePrintSections.NarrativesList]: (linkedNarratives as INarrative[])
      .slice()
      .sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateA - dateB;
      }),
    [KasePrintSections.OffenseInfo]: offenses,
  };

  const formData: IKasePrintModalForm = {
    printHeaderOnAllPages: false,
    includeSensitiveInfo: true,
    emailAddress: '',
    emailBody: '',
    recipient: '',
    reason: '',
  };

  const kasePrintFieldsRecord = createKaseFieldRecord(allModalData);

  const definition = await buildPDFDefinition({
    agencyData,
    kase,
    activeCFSData,
    user,
    formData,
    allModalData,
    kasePrintFieldsRecord,
    isEmail: false,
  });
  const headerContent = resolveHeaderContent(definition.header);

  return {
    header: headerContent,
    content: normalizeCaseSignatureSection(toContentArray(definition.content)),
    styles: definition.styles,
    defaultStyle: definition.defaultStyle,
  };
};

const buildCfsSectionDefinition = async ({
  recordId,
  agencyData,
  agencies,
  user,
  cfsCloseReasons,
}: {
  recordId: string;
  agencyData: IAgency | null;
  agencies: IAgency[];
  user: IUser | null;
  cfsCloseReasons: any[];
}): Promise<RecordSectionDefinition> => {
  const [
    cfsResponse,
    namesResponse,
    vehiclesResponse,
    narrativesResponse,
    timelineResponse,
  ] = await Promise.all([
    CfsApi.getOne(recordId),
    CfsApi.getCfsMasterNames(recordId),
    CfsApi.getCfsMasterVehicles(recordId),
    CfsApi.getLinkedNarratives(recordId, {}),
    CfsApi.getCfsHistory(recordId, {
      limit: 1000,
      sort: JSON.stringify({ createdAt: -1 }),
      filter: JSON.stringify({
        event: {
          $in: [
            CFSEvent.CREATED,
            CFSEvent.UNIT_SHIFT_ASSIGNED,
            CFSEvent.UNIT_SHIFT_IS_EN_ROUTE,
            CFSEvent.UNIT_SHIFT_IS_ON_SCENE,
            CFSEvent.CLOSED,
          ],
        },
      }),
    }),
  ]);

  const cfsTimelineLogs = timelineResponse.data?.results || [];

  const definition = await buildCFSReportDefinition({
    ctx: {
      cfs: cfsResponse.data,
      agency: agencyData as any,
      agencies,
      user,
      cfsNames: namesResponse.data || [],
      cfsVehicles: vehiclesResponse.data || [],
      narratives: narrativesResponse.data || [],
      cfsCloseReasons,
      cfsTimelineLogs,
      printedAt: new Date(),
    },
  });
  const headerContent = resolveHeaderContent(definition.header);

  return {
    header: headerContent,
    content: toContentArray(definition.content),
    styles: definition.styles,
    defaultStyle: definition.defaultStyle,
  };
};

const createCombinedDefinition = (
  sections: RecordSectionDefinition[],
  sectionPageCounts: number[],
): TDocumentDefinitions => {
  const content: Content[] = [];
  const combinedStyles: StyleDictionary = {};
  let defaultStyle: TDocumentDefinitions['defaultStyle'] = {
    fontSize: 10,
    color: '#000',
  };
  let runningPage = 1;
  const sectionPageRanges = sections.map((section, index) => {
    const pages = Math.max(sectionPageCounts[index] || 1, 1);
    const range = {
      start: runningPage,
      end: runningPage + pages - 1,
      header: section.header || null,
    };
    runningPage += pages;
    return range;
  });

  sections.forEach((section, index) => {
    if (index > 0) {
      content.push({ text: '', pageBreak: 'before' });
    }

    content.push(...cloneDeep(section.content));

    if (section.styles) {
      Object.assign(combinedStyles, section.styles);
    }

    if (section.defaultStyle) {
      defaultStyle = { ...defaultStyle, ...section.defaultStyle };
    }
  });

  return {
    content,
    styles: combinedStyles,
    defaultStyle,
    pageMargins: BATCH_PAGE_MARGINS,
    pageSize: 'A4',
    pageOrientation: 'portrait',
    header: (currentPage: number) => {
      const currentSection = sectionPageRanges.find(
        (range) => currentPage >= range.start && currentPage <= range.end,
      );
      return currentSection?.header || { text: '' };
    },
    footer: (currentPage: number, pageCount: number) => ({
      text: `Page ${currentPage} of ${pageCount}`,
      alignment: 'center',
      margin: [0, 10, 0, 0],
      fontSize: 9,
      color: '#000',
    }),
    info: {
      title: `Related Records Batch Print - ${moment().format(
        'MM/DD/YYYY HH:mm',
      )}`,
    },
  };
};

const cloneSectionForPdf = (
  section: RecordSectionDefinition,
): RecordSectionDefinition => ({
  content: cloneDeep(section.content),
  header: section.header ? cloneDeep(section.header) : null,
  styles: section.styles ? cloneDeep(section.styles) : undefined,
  defaultStyle: section.defaultStyle
    ? cloneDeep(section.defaultStyle)
    : undefined,
});

const loadPdfMakeInstance = async () => {
  const [pdfMakeModule, pdfFontsModule] = await Promise.all([
    import('pdfmake/build/pdfmake'),
    import('pdfmake/build/vfs_fonts'),
  ]);

  const pdfMakeInstance = (pdfMakeModule as any).default || pdfMakeModule;
  const fontsInstance = (pdfFontsModule as any).default || pdfFontsModule;

  if (pdfMakeInstance && fontsInstance) {
    pdfMakeInstance.vfs = fontsInstance.pdfMake?.vfs || fontsInstance;
  }

  return pdfMakeInstance;
};

const getSectionPageCount = async (
  pdfMakeInstance: any,
  section: RecordSectionDefinition,
) =>
  new Promise<number>((resolve) => {
    try {
      const sectionClone = cloneSectionForPdf(section);
      const createPdf = pdfMakeInstance.createPdf || pdfMakeInstance;
      const sectionDefinition: TDocumentDefinitions = {
        content: sectionClone.content,
        styles: sectionClone.styles || {},
        defaultStyle: sectionClone.defaultStyle || {
          fontSize: 10,
          color: '#000',
        },
        pageMargins: BATCH_PAGE_MARGINS,
        pageSize: 'A4',
        pageOrientation: 'portrait',
        header: sectionClone.header || undefined,
      };
      const pdfDocGenerator = createPdf(sectionDefinition) as any;
      if (typeof pdfDocGenerator?._getPages === 'function') {
        pdfDocGenerator._getPages({}, (pages: unknown[]) => {
          resolve(Math.max(pages?.length || 1, 1));
        });
        return;
      }
      resolve(1);
    } catch (_error) {
      resolve(1);
    }
  });

const printPdfDefinition = async (
  definition: TDocumentDefinitions,
  pdfMakeInstance?: any,
) => {
  const pdfMake = pdfMakeInstance || (await loadPdfMakeInstance());
  const createPdf = pdfMake.createPdf || pdfMake;
  createPdf(definition).print();
};

export const printRelatedRecordsBatch = async ({
  rows,
  user,
  currentAgency,
  agencies,
  fetchAgency,
}: RelatedRecordsBatchPrintParams): Promise<RelatedRecordsBatchPrintResult> => {
  const failedRecords: string[] = [];
  const sections: RecordSectionDefinition[] = [];

  if (!rows.length) {
    return { failedRecords, printedCount: 0 };
  }

  const agencyData = await getAgencyData({ currentAgency, fetchAgency });
  const closeReasonsResponse = await CfsApi.getCloseReasons().catch(() => null);
  const cfsCloseReasons = closeReasonsResponse?.data || [];

  for (const row of rows) {
    try {
      const normalizedType = normalizeRecordType(row.type);
      if (isCaseRecord(normalizedType)) {
        const caseSection = await buildCaseSectionDefinition({
          recordId: row._id,
          agencyData,
          user,
        });
        sections.push(caseSection);
        continue;
      }

      if (isCfsRecord(normalizedType)) {
        const cfsSection = await buildCfsSectionDefinition({
          recordId: row._id,
          agencyData,
          agencies,
          user,
          cfsCloseReasons,
        });
        sections.push(cfsSection);
        continue;
      }

      failedRecords.push(row.name || row._id);
    } catch (_error) {
      failedRecords.push(row.name || row._id);
    }
  }

  if (!sections.length) {
    return { failedRecords, printedCount: 0 };
  }

  const pdfMakeInstance = await loadPdfMakeInstance();
  const sectionPageCounts = await Promise.all(
    sections.map((section) => getSectionPageCount(pdfMakeInstance, section)),
  );
  const combinedDefinition = createCombinedDefinition(
    sections,
    sectionPageCounts,
  );
  await printPdfDefinition(combinedDefinition, pdfMakeInstance);

  return {
    failedRecords,
    printedCount: sections.length,
  };
};
